import { startAuthentication } from "@simplewebauthn/browser";
import type { PublicKeyCredentialRequestOptionsJSON } from "@simplewebauthn/browser";
import { useQueryClient } from "@tanstack/react-query";
import { KeyRound } from "lucide-react";
import { useState } from "react";
import type { MeDTO } from "../../shared/dto.js";
import { api, ApiError } from "@/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";

export function LoginPage({ me }: { me: MeDTO }) {
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function signIn() {
    setBusy(true);
    setError(null);
    try {
      const options = await api.post<PublicKeyCredentialRequestOptionsJSON>("/api/auth/login/options");
      const response = await startAuthentication({ optionsJSON: options });
      await api.post("/api/auth/login/verify", { response });
      await queryClient.invalidateQueries();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : e instanceof Error && e.name === "NotAllowedError" ? "Sign-in was cancelled." : "Couldn't sign in. Try again.");
    } finally {
      setBusy(false);
    }
  }

  async function devSignIn() {
    await api.post("/api/auth/dev-login");
    await queryClient.invalidateQueries();
  }

  return (
    <main className="grid min-h-screen place-items-center p-6">
      <Card className="w-full max-w-sm p-8">
        <div className="mb-6 flex items-center gap-3">
          <img src="/favicon.svg" alt="" className="size-10" />
          <div>
            <h1 className="text-lg font-semibold">NS Journal</h1>
            <p className="text-sm text-muted">Glucose and journal, read-only</p>
          </div>
        </div>
        <Button variant="primary" size="lg" className="w-full" onClick={signIn} disabled={busy}>
          <KeyRound /> Sign in with a passkey
        </Button>
        {error && <p className="mt-3 text-sm text-danger">{error}</p>}
        {me.needsEnrollment && (
          <p className="mt-6 text-sm text-muted">
            No passkey yet. Run <code className="rounded bg-surface-muted px-1">fly ssh console -C "node dist/server/cli.js enroll"</code> and open the link it prints.
          </p>
        )}
        {me.devLogin && (
          <Button variant="ghost" className="mt-4 w-full" onClick={devSignIn}>
            Dev sign-in
          </Button>
        )}
        <p className="mt-6 border-t border-line pt-4 text-xs text-muted">Someone shared a link with you? Open the link itself: it signs this browser in, read-only.</p>
      </Card>
    </main>
  );
}
